import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";

const PHASES = [
  { label: "Breathe In", duration: 4000, scale: 1.6, color: "#3b82f6" },
  { label: "Hold", duration: 4000, scale: 1.6, color: "#8b5cf6" },
  { label: "Breathe Out", duration: 6000, scale: 1, color: "#10b981" },
  { label: "Hold", duration: 2000, scale: 1, color: "#f59e0b" },
];
const TOTAL_CYCLE = PHASES.reduce((s, p) => s + p.duration, 0);

export default function BreathingPacer({ onComplete }) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [taps, setTaps] = useState([]);
  const [finished, setFinished] = useState(false);
  const targetCycles = 5;
  const animRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!playing) return;
    startRef.current = Date.now();
    const tick = () => {
      const now = Date.now() - startRef.current;
      setElapsed(now);
      const completedCycles = Math.floor(now / TOTAL_CYCLE);
      setCycles(completedCycles);
      if (completedCycles >= targetCycles) {
        setPlaying(false);
        setFinished(true);
        return;
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [playing]);

  const currentPhaseTime = elapsed % TOTAL_CYCLE;
  let accum = 0, phase = PHASES[0], phaseProgress = 0;
  for (const p of PHASES) {
    if (currentPhaseTime < accum + p.duration) {
      phase = p;
      phaseProgress = (currentPhaseTime - accum) / p.duration;
      break;
    }
    accum += p.duration;
  }

  const scale = phase.label === "Breathe In" ? 1 + 0.6 * phaseProgress : phase.label === "Breathe Out" ? 1.6 - 0.6 * phaseProgress : phase.scale;

  const handleTap = () => {
    if (playing) setTaps(prev => [...prev, { time: elapsed, phase: phase.label }]);
  };

  const score = finished ? Math.max(0, Math.min(100, Math.round(60 + (taps.length / targetCycles) * 8 + cycles * 4))) : 0;

  if (finished) {
    return (
      <div data-testid="breathing-result" className="text-center py-8 space-y-6">
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto shadow-xl">
          <span className="text-3xl font-bold text-white">{score}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Session Complete</h3>
          <p className="text-gray-500">{targetCycles} breathing cycles completed</p>
          <p className="text-sm text-gray-400 mt-1">{taps.length} mindful taps during session</p>
        </div>
        <Button data-testid="save-breathing-score" onClick={() => onComplete(score, targetCycles * TOTAL_CYCLE / 1000)} className="bg-emerald-600 hover:bg-emerald-700">
          Save Score & Return
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="breathing-pacer-game" className="flex flex-col items-center py-6 space-y-6">
      {!playing ? (
        <>
          <p className="text-sm text-gray-600 text-center max-w-md">Follow the breathing circle. Tap the circle each time you transition to a new phase to track your mindfulness.</p>
          <Button data-testid="start-breathing" onClick={() => setPlaying(true)} className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-3">Start Breathing</Button>
        </>
      ) : (
        <>
          <div className="text-center">
            <p className="text-xs text-gray-500">Cycle {Math.min(cycles + 1, targetCycles)} of {targetCycles}</p>
            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1 overflow-hidden" style={{ width: 200 }}>
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${(cycles / targetCycles) * 100}%` }} />
            </div>
          </div>

          <button
            onClick={handleTap}
            className="relative w-48 h-48 rounded-full transition-transform duration-500 ease-in-out flex items-center justify-center shadow-2xl cursor-pointer"
            style={{ transform: `scale(${scale})`, backgroundColor: phase.color }}
          >
            <span className="text-white text-lg font-bold">{phase.label}</span>
          </button>

          <p className="text-sm text-gray-500">Taps: {taps.length}</p>
        </>
      )}
    </div>
  );
}
